import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  PenLine, 
  Clock, 
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { API, authFetch } from '../lib/api';

export const DraftsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      login();
    }
  }, [authLoading, user, login]);

  useEffect(() => {
    if (user) {
      fetchDrafts();
    }
  }, [user]);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/articles/drafts?limit=50`);
      
      if (response.ok) {
        const data = await response.json();
        setDrafts(data);
      }
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!draftToDelete) return;
    
    try {
      const response = await authFetch(`${API}/articles/${draftToDelete}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setDrafts(prev => prev.filter(d => d.article_id !== draftToDelete));
        toast.success('Draft deleted');
      } else {
        toast.error('Failed to delete draft');
      }
    } catch (error) {
      toast.error('Failed to delete draft');
    }
    
    setDeleteDialogOpen(false);
    setDraftToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(String(dateString).replace(' ', 'T'));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="drafts-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 fade-up">
          <div>
            <h1 
              className="text-3xl sm:text-4xl font-bold mb-2"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Your Drafts
            </h1>
            <p className="text-muted-foreground">
              {drafts.length} unpublished {drafts.length === 1 ? 'story' : 'stories'}
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/write')}
            className="rounded-none"
            data-testid="new-story-btn"
          >
            <PenLine className="w-4 h-4 mr-2" />
            New story
          </Button>
        </div>

        {/* Drafts List */}
        <div className="space-y-4 stagger-children">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="p-6 bg-paper rounded-lg border border-border">
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))
          ) : drafts.length > 0 ? (
            drafts.map(draft => (
              <div 
                key={draft.article_id}
                className="p-6 bg-paper rounded-lg border border-border hover:border-accent/50 transition-colors group"
                data-testid={`draft-${draft.article_id}`}
              >
                <div className="flex items-start justify-between">
                  <Link 
                    to={`/write/${draft.article_id}`}
                    className="flex-1"
                  >
                    <h3 
                      className="text-xl font-semibold mb-2 group-hover:text-accent transition-colors"
                      style={{ fontFamily: 'Fraunces, serif' }}
                    >
                      {draft.title || 'Untitled'}
                    </h3>
                    
                    {draft.subtitle && (
                      <p className="text-muted-foreground mb-3 line-clamp-2">
                        {draft.subtitle}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        Last edited {formatDate(draft.updated_at)}
                      </span>
                      <span>{draft.reading_time} min read</span>
                    </div>
                  </Link>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`draft-menu-${draft.article_id}`}
                        aria-label="Draft actions"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/write/${draft.article_id}`)}>
                        <PenLine className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setDraftToDelete(draft.article_id);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive"
                        data-testid={`draft-delete-${draft.article_id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          ) : (
            <div className="py-16 text-center">
              <PenLine className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-2">
                No drafts yet
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Start writing and your drafts will appear here
              </p>
              <Button
                onClick={() => navigate('/write')}
                className="rounded-none"
              >
                Write your first story
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This draft will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DraftsPage;
